import type { ComponentType } from 'react'
import type { ResourceScope } from '../../api/resources'

/**
 * Props for a kind's edit-mode extras panel (see `EditExtras`). Kinds whose
 * data is managed out-of-band (todo items via their own endpoints) own that UI
 * and its mutations here, instead of the form page special-casing them.
 */
export interface ResourceEditExtrasProps {
  scope: ResourceScope
  resourceId: string
  /** The stored resource `data` blob. */
  data: unknown
}

/** Form field values are normalized to strings; booleans/numbers convert at the kind boundary. */
export type Fields = Record<string, string>

/** Update one field in the parent form state. */
type SetField = (key: string, value: string) => void

/** Props every per-kind field block receives from the form page. */
export interface FieldProps {
  fields: Fields
  set: SetField
  isEdit: boolean
  /** The scope the form is adding/updating in. Kinds whose setup needs
   * context beyond the fields (github's one-click flow needs the workspace
   * and a topic picker) read it from here. */
  scope?: ResourceScope
}

/**
 * A registered form for one resource kind: the inputs to render, plus the pair
 * of pure serializers that move between form state and the request/stored
 * shapes. This mirrors the backend `ResourceHandler` trait split (config UI vs.
 * persistence) and removes the per-kind switches that used to live inline in
 * `ResourceFormPage`.
 */
export interface ResourceFormDescriptor {
  /** Render the kind-specific inputs inside the parent `<form>`. `null` when the kind has no config fields (e.g. todo_list). */
  Fields: ComponentType<FieldProps> | null
  /** Form fields → request `data` body. */
  toBody: (fields: Fields) => unknown
  /** Stored `data` → form fields. */
  fromResource: (data: unknown) => Fields
  /** Kinds whose `data` is managed out-of-band (todo items via separate endpoints) omit `data` on update. */
  skipDataOnUpdate?: boolean
  /** Optional edit-mode panel for out-of-band data (e.g. todo items), owning its own mutations. */
  EditExtras?: ComponentType<ResourceEditExtrasProps>
  /** Replace the form's create submission for this kind (github's one-click
   * App setup provisions via the manifest-flow API instead of a direct
   * resource create). Return `false` to fall back to the default create —
   * one descriptor can serve both setup styles. */
  createVia?: (fields: Fields, scope: ResourceScope) => Promise<boolean | void>
  /** On create, whether the setup flow names the resource itself (the
   * Name input is hidden and not required). */
  namedByFlow?: (fields: Fields) => boolean
}

/** Coerce a stored value to its form-field string, defaulting when absent. */
export function fieldStr(data: unknown, key: string, fallback = ''): string {
  const r = (data ?? {}) as Record<string, unknown>
  const v = r[key]
  return v == null ? fallback : String(v)
}

/** Form-state key holding the stored `data` as JSON, so an edit keeps the
 * keys a form doesn't render (normalize rules, knobs, …): the update API
 * replaces `data` wholesale. */
export const STORED = '__stored'

/** Stored keys never resent: plaintext secrets and their sealed forms
 * (a blank secret input means "keep" — the server carries `*_enc` forward). */
const isSecretKey = (k: string) =>
  k.endsWith('_enc') ||
  k === 'token' ||
  k === 'private_key' ||
  k === 'webhook_secret' ||
  k === 'secret'

export function storedData(f: Fields): Record<string, unknown> {
  return f[STORED] ? (JSON.parse(f[STORED]) as Record<string, unknown>) : {}
}

/** The stored keys a form doesn't manage, minus secrets. */
export function preservedKeys(f: Fields): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(storedData(f)).filter(([k]) => !isSecretKey(k)),
  )
}
